import { useEffect, useState, useRef } from "react";

type TranslationDetectionResult = {
  isTranslationDetected: boolean
  isChecking: boolean
};

const TRANSLATION_TRAP_TEXT = "bonjour hello hola ciao hallo";
const DETECTION_DELAY = 500;
const CHECK_INTERVAL = 1000;
const MAX_CHECKS = 5;

export const useTranslationDetection = (): TranslationDetectionResult => {
  const [isTranslationDetected, setIsTranslationDetected] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const trapElementRef = useRef<HTMLDivElement | null>(null);
  const checkCountRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const createTrapElement = (): HTMLDivElement => {
    const trapElement = document.createElement("div");
    trapElement.innerHTML = TRANSLATION_TRAP_TEXT;
    trapElement.style.position = "absolute";
    trapElement.style.left = "-9999px";
    trapElement.style.top = "-9999px";
    trapElement.style.width = "1px";
    trapElement.style.height = "1px";
    trapElement.style.overflow = "hidden";
    trapElement.style.visibility = "hidden";
    trapElement.style.opacity = "0";
    trapElement.setAttribute("aria-hidden", "true");
    trapElement.setAttribute("data-translation-trap", "true");

    return trapElement;
  };

  const checkForTranslation = (): boolean => {
    if (!trapElementRef.current) return false;

    const currentText = trapElementRef.current.textContent || "";
    const isTranslated = currentText.trim() !== TRANSLATION_TRAP_TEXT;

    return isTranslated;
  };

  const startDetection = () => {
    setIsChecking(true);
    checkCountRef.current = 0;

    const trapElement = createTrapElement();
    document.body.appendChild(trapElement);
    trapElementRef.current = trapElement;

    const startCheckingTimeout = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        checkCountRef.current++;

        const translationDetected = checkForTranslation();

        if (translationDetected) {
          setIsTranslationDetected(true);
          setIsChecking(false);

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }

        if (checkCountRef.current >= MAX_CHECKS) {
          setIsChecking(false);

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, CHECK_INTERVAL);
    }, DETECTION_DELAY);

    return () => {
      clearTimeout(startCheckingTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  };

  const cleanup = () => {
    trapElementRef?.current?.parentNode?.removeChild(trapElementRef.current);
    trapElementRef.current = null;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    const cleanupFn = startDetection();

    return () => {
      cleanupFn?.();
      cleanup();
    };
  }, []);

  return {
    isTranslationDetected,
    isChecking,
  };
};
